import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Typography } from '@telus-uds/components-web';
import Download from '@telus-uds/palette-allium/build/web/icons/Download';

import request, { apiHost } from '../../../../utils/request';
import { useLocaleContext } from '../../../../context/LocaleContext';
import useOrderPdf from '../../../../hooks/useOrderPdf';
import { ORDER_PDF_STATUS } from '../../../../utils/orderPdf';
import { FieldWrapper } from '../css';
import FilesList from './FilesList';
import FileUpload from './FileUpload';
// NOTE: PdfGenerationStatus (T034/T035/ARCH-308) is retired here on purpose — it polled the coarse
// order-level GET /orders/:id/pdf-status ("latest pdf_email_jobs row for the order"), which a LATER
// unrelated job (e.g. the customer/sales notification jobs enqueued right after a signed-contract
// freeze) can keep reporting PENDING/PROCESSING long after the actual Quote/Contract artifact is
// AVAILABLE — the exact "stuck on Generating" bug. Each PdfLinkRow below now resolves its OWN
// artifact's status directly, which is already correct (see orderPdfAccess/resolveSignedContractAccess).

const listItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};
const transparentStyle = { backgroundColor: 'transparent' };
const statusTextStyle = { marginRight: '0.75rem', fontSize: '0.85rem' };

// User-safe copy for the worker-only async PDF flow (no job/S3/hash internals ever surfaced).
const PDF_COPY = {
  PREPARING: 'Preparing PDF...',
  FAILED: 'PDF could not be generated. Please try again.',
  NOT_READY: 'PDF is not ready yet.'
};

/**
 * One downloadable PDF row. Resolves its OWN artifact's status (per-row, not a shared order-level
 * badge): auto-polls on mount (no auto-open) so it can show "Preparing PDF..." and flip to the
 * download icon the moment ITS artifact is AVAILABLE, with no further requests afterward. Clicking
 * routes through the SAME shared async order-PDF client to actually open it. Never window.open's the
 * protected backend route directly.
 */
const PdfLinkRow = ({ orderId, label, route }) => {
  const pdf = useOrderPdf();
  const preparing = pdf.status === ORDER_PDF_STATUS.PREPARING;
  const message =
    (pdf.status === ORDER_PDF_STATUS.PREPARING && PDF_COPY.PREPARING) ||
    (pdf.status === ORDER_PDF_STATUS.FAILED && PDF_COPY.FAILED) ||
    (pdf.status === ORDER_PDF_STATUS.NOT_READY && PDF_COPY.NOT_READY) ||
    '';

  useEffect(() => {
    // openPdf:false — resolve/poll status only, never auto-open a new tab on mount.
    pdf.openPdf({ orderId, route, openPdf: false });
    return pdf.cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, route]);

  return (
    <li className="list-group-item" style={listItemStyle}>
      <div style={transparentStyle}>{label}</div>
      <div style={transparentStyle}>
        {message && <span style={statusTextStyle}>{message}</span>}
        <Button
          icon={Download}
          onPress={() => {
            if (!preparing) pdf.openPdf({ orderId, route });
          }}
          accessibilityLabel="Download"
          variant={{ text: true }}
        />
      </div>
    </li>
  );
};

PdfLinkRow.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  route: PropTypes.string.isRequired
};

/**
 * Shared Attachments container. Accepts config for PDF download links.
 * @param {Object} orderData - Order object with at least { id }
 * @param {Array} pdfLinks - [{ label: string, getUrl: (orderId, locale, apiHost) => string }]
 * @param {string} cardHeaderTitle - Optional title for the PDF card header (e.g. "Quote/Contract PDF" or "Quote PDF")
 */
const Attachments = ({
  orderData,
  pdfLinks = [],
  cardHeaderTitle = 'Quote/Contract PDF',
  hideUpload = false
}) => {
  const locale = useLocaleContext();
  const { id: orderId } = orderData;
  const [filesList, setFilesList] = useState([]);

  useEffect(() => {
    request.get(`/orders/${orderId}`).then(data => {
      setFilesList(data.data.attachments || []);
    });
  }, [orderId]);

  const addFileToList = newFiles => {
    setFilesList(prev => [...prev, ...newFiles]);
  };

  const removeFileFromList = id => {
    setFilesList(prev => prev.filter(file => file.id !== id));
  };

  return (
    <>
      <Divider space={4} variant={{ inverse: true }} />
      <Typography heading="h2" variant={{ size: 'h2' }} block>
        Attachments
      </Typography>
      {pdfLinks.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="card-header">
            <Typography heading="h4" variant={{ size: 'h4' }} block>
              {cardHeaderTitle}
            </Typography>
          </div>
          <ul className="list-group list-group-flush">
            {pdfLinks.map(({ label, getUrl }) => (
              <PdfLinkRow
                key={label}
                orderId={orderId}
                label={label}
                route={getUrl(orderId, locale, apiHost)}
              />
            ))}
          </ul>
        </div>
      )}
      <FieldWrapper>
        {filesList.length > 0 && (
          <FilesList
            filesList={filesList}
            orderId={orderId}
            removeFileFromList={removeFileFromList}
          />
        )}
        {!hideUpload && (
          <FileUpload addFileToList={addFileToList} orderId={orderId} />
        )}
      </FieldWrapper>
    </>
  );
};

Attachments.propTypes = {
  orderData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string
  }).isRequired,
  pdfLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      getUrl: PropTypes.func.isRequired
    })
  ),
  cardHeaderTitle: PropTypes.string,
  hideUpload: PropTypes.bool
};

export default Attachments;
export { PdfLinkRow };
